import { useEffect, useState } from 'react'
import { useT } from '@/i18n'
import { useToast } from '@/providers/UiProviders'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { ApiError } from '@/lib/api'
import { launchAuction, type AdminGiftAuction, type AdminGiftRow } from './giftApi'

export function LaunchAuctionDialog({
  gift,
  onClose,
  onLaunched,
}: {
  gift: AdminGiftRow | null
  onClose: () => void
  onLaunched: (auction: AdminGiftAuction) => void
}) {
  const t = useT()
  const { toast } = useToast()

  const [minBid, setMinBid] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [hours, setHours] = useState('24')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!gift) return
    setMinBid(String(gift.stars || ''))
    setQuantity('1')
    setHours('24')
    setError(null)
  }, [gift])

  if (!gift) return null

  async function handleSubmit() {
    if (!gift) return
    setError(null)
    const minBidNum = Number(minBid)
    const quantityNum = Number(quantity)
    const hoursNum = Number(hours)
    if (!minBidNum || minBidNum <= 0 || !quantityNum || quantityNum <= 0 || !hoursNum || hoursNum <= 0) {
      setError(t.gifts.fillRequired)
      return
    }

    setSubmitting(true)
    try {
      const auction = await launchAuction(gift.gift_id, {
        min_bid_amount: minBidNum,
        gifts_available: quantityNum,
        duration_seconds: Math.round(hoursNum * 3600),
      })
      toast(t.gifts.launchSuccess, { tone: 'success' })
      onLaunched(auction)
      onClose()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={Boolean(gift)}
      onClose={onClose}
      title={t.gifts.launchAuctionTitle}
      description={`${gift.title} · ${t.gifts.launchAuctionDesc}`}
      width="sm"
      footer={
        <>
          <div className="flex-1" />
          <Button variant="default" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {t.gifts.launchAuction}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t.gifts.minBidAmount} required>
          <Input type="number" min={1} value={minBid} onChange={(e) => setMinBid(e.target.value)} />
        </Field>
        <Field label={t.gifts.giftsAvailable} required>
          <Input type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </Field>
        <Field label={t.gifts.durationHours} required>
          <Input type="number" min={1} value={hours} onChange={(e) => setHours(e.target.value)} />
        </Field>
        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Dialog>
  )
}
